// CareLogix™ ORACLE — FundingApplicationAssistant (Phase 33 §4)
// LOGIX-2 powered application checklist + executive summary generator (deterministic).
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { fundingApi } from "@/api/phase33Client";

function generateSummary(opp) {
  if (!opp) return "";
  return [
    `${opp.source} is offering funding aligned with the ${opp.audience.replace(/_/g, " ")} segment in the ${opp.region} region.`,
    `Eligibility band: ${opp.bands.eligibility_band.toUpperCase()} (score ${opp.scores.eligibility_score}/100).`,
    `Fit score: ${opp.scores.fit_score}/100 — narrative should emphasize measurable outcomes and aligned community impact.`,
    `Probability band: ${opp.bands.probability_band.toUpperCase()} (${opp.scores.probability_score}%). Readiness: ${opp.scores.readiness_score}/100.`,
    `Award envelope: $${opp.award_range_usd[0].toLocaleString()} – $${opp.award_range_usd[1].toLocaleString()}.`,
    `Deadline: ${opp.days_to_deadline} days from today (HITL gate required).`,
    `LOGIX-2 recommendation: ${opp.scores.priority_score >= 70 ? "Strong pursuit candidate." : (opp.scores.priority_score >= 50 ? "Consider with conditions." : "Lower priority for current cycle.")}`,
  ].join("\n\n");
}

export default function FundingApplicationAssistant({ opportunityId, testid = "funding-application-assistant" }) {
  const [opps, setOpps] = useState([]);
  const [pickedId, setPickedId] = useState(opportunityId || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fundingApi.opportunities({ bucket: "all", limit: 30 })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.opportunities || [];
        setOpps(list);
        if (!pickedId && list.length) setPickedId(list[0].opportunity_id);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opp = useMemo(() => opps.find((o) => o.opportunity_id === pickedId) || null, [opps, pickedId]);
  const summary = useMemo(() => generateSummary(opp), [opp]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>FUNDING APPLICATION ASSISTANT™ · LOGIX-2</div>
          <h3 style={S.title}>Eligibility + Executive Summary + Checklist</h3>
        </div>
        <select value={pickedId} onChange={(e) => setPickedId(e.target.value)} style={S.select} data-testid={`${testid}-picker`}>
          {opps.map((o) => <option key={o.opportunity_id} value={o.opportunity_id}>{o.title}</option>)}
        </select>
      </header>

      {loading && <div style={S.loading}>Loading opportunities…</div>}

      {opp && (
        <div style={S.grid}>
          <motion.article style={S.card} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`${testid}-eligibility`}>
            <h4 style={S.cardTitle}>Eligibility Snapshot</h4>
            <div style={S.scoreGrid}>
              {[
                ["Eligibility", opp.scores.eligibility_score],
                ["Fit",         opp.scores.fit_score],
                ["Probability", opp.scores.probability_score],
                ["Readiness",   opp.scores.readiness_score],
                ["Priority",    opp.scores.priority_score],
                ["Match",       opp.scores.match_score],
              ].map(([k, v]) => (
                <div key={k} style={S.scoreCell}>
                  <div style={S.scoreHead}>
                    <span style={S.scoreLbl}>{k}</span>
                    <span style={{ ...S.scoreVal, color: scoreColor(v) }}>{v}</span>
                  </div>
                  <div style={S.bar}><div style={{ ...S.barFill, width: `${v}%`, background: scoreColor(v) }} /></div>
                </div>
              ))}
            </div>
            <ul style={S.meta}>
              <li><strong>Source:</strong> {opp.source}</li>
              <li><strong>Region:</strong> {opp.region}</li>
              <li><strong>Audience:</strong> {opp.audience.replace(/_/g, " ")}</li>
              <li><strong>Award:</strong> ${opp.award_range_usd[0].toLocaleString()} – ${opp.award_range_usd[1].toLocaleString()}</li>
              <li><strong>Deadline:</strong> {opp.days_to_deadline} days</li>
            </ul>
          </motion.article>

          <motion.article style={S.card} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`${testid}-summary`}>
            <h4 style={S.cardTitle}>LOGIX-2 Executive Summary</h4>
            <pre style={S.summary}>{summary}</pre>
          </motion.article>

          <motion.article style={{ ...S.card, gridColumn: "1 / -1" }} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`${testid}-checklist`}>
            <h4 style={S.cardTitle}>Required Documents Checklist</h4>
            <ul style={S.checklist}>
              {(opp.required_documents || []).map((d) => (
                <li key={d} style={S.check}>
                  <span style={S.checkBox} aria-hidden="true">▢</span>
                  {d}
                </li>
              ))}
            </ul>
            <p style={S.compliance}>Research only · Not award guarantee · HITL gate required · No autonomous submission.</p>
          </motion.article>
        </div>
      )}
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 15 },
  select:   { background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 10px", borderRadius: 8, maxWidth: 320 },
  loading:  { color: PALETTE.slate4, fontSize: 12.5 },
  grid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 },
  card:     { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  cardTitle:{ margin: 0, color: PALETTE.goldBright, fontSize: 13.5 },
  scoreGrid:{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 6 },
  scoreCell:{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 6 },
  scoreHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  scoreLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  scoreVal: { fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800 },
  bar:      { marginTop: 4, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  barFill:  { height: "100%" },
  meta:     { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4, fontSize: 12 },
  summary:  { margin: 0, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  checklist:{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  check:    { display: "flex", alignItems: "center", gap: 8, color: PALETTE.slate3, fontSize: 13 },
  checkBox: { color: PALETTE.gold, fontSize: 16 },
  compliance:{ margin: 0, color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10 },
};
